import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import i18next from 'i18next';
import { useMemo } from 'react';

import { useTenant } from '../hooks/routing/TenantProvider';
import { useUser } from '../hooks/useUser';
import { httpClient } from '../http-client';

export const blobToBase64 = (blob) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise((resolve) => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

export const getBlobAuthed = async (
  url: string,
  tokenOrHeaders: string | Record<string, string>,
  tenant?: number
) => {
  return await httpClient.get<BlobPart>({
    url: url,
    responseType: 'blob',
    headers:
      typeof tokenOrHeaders === 'string'
        ? {
            Authorization: `bearer ${tokenOrHeaders}`,
            mandantenCode: `${tenant}`,
          }
        : tokenOrHeaders,
  });
};

export const getObjectUrlAuthed = async (
  url: string,
  tokenOrHeaders: string | Record<string, string>,
  tenant?: number
) => {
  const response = await getBlobAuthed(url, tokenOrHeaders, tenant);
  return window.URL.createObjectURL(new Blob([response.data]));
};

const downloadAuthedMobile = async (
  url: string,
  fileName: string,
  tokenOrHeaders: string | Record<string, string>,
  tenant?: number
) => {
  try {
    // Get the file data as a blob
    const response = await getBlobAuthed(url, tokenOrHeaders, tenant);

    // Convert blob to base64 for Capacitor Filesystem
    const base64Data = await blobToBase64(response.data as Blob);

    // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
    const base64Content = (base64Data as string).split(',')[1];

    // Write file to a temporary cache location first
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64Content,
      directory: Directory.Cache,
    });

    // Use native share functionality to let user choose where to save
    await Share.share({
      title: 'Save File',
      text: `Download: ${fileName}`,
      url: result.uri,
      dialogTitle: 'Choose where to save the file',
    });

    return result;
  } catch (error) {
    console.error('Error downloading file on mobile:', error);

    // Show error message - only if sharing failed
    if (Capacitor.isNativePlatform()) {
      // eslint-disable-next-line no-alert
      alert(
        error instanceof Error
          ? i18next.t('ALERTS.DOWNLOAD_FILE_FAILED', {
              fileName,
              error: error.message,
            })
          : i18next.t('ALERTS.ERROR.UNKNOWN')
      );
    }
  }
};

export const downloadAuthed = async (
  url: string,
  fileName: string,
  tokenOrHeaders: string | Record<string, string>,
  tenant?: number
) => {
  const platform = Capacitor.getPlatform();

  if (platform === 'web') {
    // Web platform: use the original blob URL + anchor approach
    const objectUrl = await getObjectUrlAuthed(url, tokenOrHeaders, tenant);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', fileName);
    link.setAttribute('id', 'temp-dl-inject');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } else {
    // Mobile platforms (iOS/Android): use Capacitor Filesystem
    await downloadAuthedMobile(url, fileName, tokenOrHeaders, tenant);
  }
};

export const useDeafultPreviewHeaders = () => {
  const { activeTenant: tenant } = useTenant();
  const user = useUser();
  const bearerToken = user.token;
  return useMemo(
    () => ({
      Authorization: `bearer ${bearerToken}`,
      mandantenCode: tenant.toString(),
    }),
    [bearerToken, tenant]
  );
};
